import { cn } from '../../../shared/utils/cn'
import type { SelfTestReport } from '../../../types/selfTest'

interface SelfTestSummaryProps {
  report: SelfTestReport
}

function Stat({ label, value, className }: { label: string; value: string | number; className?: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className={cn('text-2xl font-semibold text-gray-900', className)}>{value}</p>
    </div>
  )
}

function PoolDot({ label, state }: { label: string; state: 'up' | 'down' }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-gray-700">
      <span
        className={cn('inline-block h-2.5 w-2.5 rounded-full', state === 'up' ? 'bg-green-500' : 'bg-red-500')}
      />
      {label} {state}
    </span>
  )
}

export function SelfTestSummary({ report }: SelfTestSummaryProps) {
  const { build, totals, latencyStats, fixtures } = report

  return (
    <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Build v{build.version}{' '}
            <span className="font-mono text-sm font-normal text-gray-600">{build.gitHash}</span>
            {build.branch && <span className="text-sm font-normal text-gray-500"> · {build.branch}</span>}
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            {build.buildTime ? `Built ${build.buildTime} · ` : ''}
            ran in {(report.durationMs / 1000).toFixed(1)}s
            {report.cached && report.cachedAt ? ` · cached result from ${report.cachedAt}` : ''}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <PoolDot label="main" state={report.pools.main} />
          <PoolDot label="analytics" state={report.pools.analytics} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
        <Stat label="Pass" value={totals.pass} className="text-green-700" />
        <Stat
          label="Fail"
          value={totals.fail}
          className={totals.fail > 0 ? 'text-red-700' : undefined}
        />
        <Stat label="Not covered" value={totals.notCovered} className="text-gray-500" />
        <Stat label="Endpoints" value={totals.total} />
      </div>

      <div className="border-t border-gray-200 pt-4">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Latency</h3>
        <div className="flex flex-wrap gap-6 text-sm text-gray-700">
          <span>
            p50 <span className="font-medium tabular-nums">{latencyStats.p50} ms</span>
          </span>
          <span>
            p95 <span className="font-medium tabular-nums">{latencyStats.p95} ms</span>
          </span>
          <span>
            max <span className="font-medium tabular-nums">{latencyStats.max} ms</span>
          </span>
        </div>

        {latencyStats.slowest.length > 0 && (
          <table className="w-full text-sm mt-3">
            <thead className="text-left text-xs uppercase text-gray-500 border-b border-gray-200">
              <tr>
                <th className="py-2 pr-4">Slowest endpoints</th>
                <th className="py-2 pr-4 text-right">Latency</th>
              </tr>
            </thead>
            <tbody>
              {latencyStats.slowest.map((entry) => (
                <tr key={entry.path} className="border-b border-gray-100 last:border-0">
                  <td className="py-2 pr-4 font-mono text-gray-700">{entry.path}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{entry.latencyMs} ms</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <p className="mt-2 text-xs text-gray-500">
          Measured over loopback, so network and TLS are excluded. Probes run {report.config.concurrency} at a
          time and therefore contend with each other — use these to spot outliers and regressions, not as
          benchmarks.
        </p>
      </div>

      <div className="border-t border-gray-200 pt-4">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Run context</h3>
        <p className="text-sm text-gray-600">
          Probed as {report.identity.email ?? `user ${report.identity.userId}`} against instance{' '}
          <span className="font-mono">{report.instance.hostname}:{report.instance.port}</span> (pid{' '}
          {report.instance.pid}). Loopback reaches only this instance.
        </p>
        {(() => {
          // Skip blanks — an empty accountId rendered as 'accountId=' reads as
          // a bug in the panel rather than an absent claim on the token.
          const shown = Object.entries(fixtures.resolved).filter(
            ([, value]) => value !== '' && value !== null && value !== undefined
          )
          if (shown.length === 0) return null
          return (
            <p className="mt-1 text-sm text-gray-600">
              Test data ({fixtures.strategy}):{' '}
              {shown.map(([key, value]) => `${key}=${value}`).join(', ')}
            </p>
          )
        })()}
        {Object.keys(fixtures.unresolved).length > 0 && (
          <ul className="mt-2 space-y-0.5 text-sm text-gray-500">
            {Object.entries(fixtures.unresolved).map(([key, reason]) => (
              <li key={key}>
                <span className="font-medium">{key}</span> unavailable — {reason}
              </li>
            ))}
          </ul>
        )}
        {report.enumeration.warnings.length > 0 && (
          <ul className="mt-2 space-y-0.5 text-sm text-amber-700">
            {report.enumeration.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        )}
        {report.totals.noProbeDeclared > 0 && (
          <p className="mt-2 text-sm text-gray-500">
            {report.totals.noProbeDeclared} route(s) have no probe declared yet.
          </p>
        )}
      </div>
    </section>
  )
}
